import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';
import { authErrorMessage } from '../utils/authErrors';

export default function Login() {
  const { login, loginWithGoogle, resetPassword } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from || '/';

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(authErrorMessage(err, 'Login failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error('Enter your email above first, then click "Forgot password?"');
      return;
    }
    try {
      await resetPassword(email);
    } catch (err) {
      if (err.code === 'auth/invalid-email') {
        toast.error('That email address looks invalid');
        return;
      }
      // Any other error (including unknown account) gets the same message so
      // this can't be used to check which emails have accounts.
    }
    toast.success('If an account exists for that email, a reset link has been sent.');
  };

  const handleGoogle = async () => {
    try {
      await loginWithGoogle();
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(authErrorMessage(err, 'Google sign-in failed. Please try again.'));
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <img src="/logo.png" alt="Swift & Sure Exim" className="auth-logo" />
        <h1 className="auth-title">Admin Sign In</h1>
        <p className="auth-subtitle">Swift &amp; Sure Exim — Admin Panel</p>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-field">
            <label>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" required />
          </div>
          <div className="form-field">
            <label>Password</label>
            <div className="password-input">
              <input type={showPassword ? 'text' : 'password'} value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
              <button type="button" className="password-toggle"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                title={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
          </div>
          <div style={{ textAlign: 'right', marginBottom: 12 }}>
            <button type="button" className="link-btn" onClick={handleForgotPassword}>
              Forgot password?
            </button>
          </div>
          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="auth-divider"><span>or</span></div>

        <button className="btn btn-outline btn-full google-btn" onClick={handleGoogle}>
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" width={20} />
          Continue with Google
        </button>

        <p className="auth-footer">
          Admin access is granted from the Users page — there's no self-registration here.
        </p>
      </div>
    </div>
  );
}
